import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { adminApi, type AdminCompanyDetail } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { X, Save, Users, FileText, UserCircle } from "lucide-react";

interface AdminCompanyDetailModalProps {
  companyId: string;
  onClose: () => void;
  onUpdated: () => void;
}

const TYPE_OPTIONS = [
  { value: "AB", label: "AB" },
  { value: "HB", label: "HB" },
  { value: "KB", label: "KB" },
  { value: "EF", label: "EF" },
];

const statusLabels: Record<string, string> = {
  draft: "Utkast",
  in_progress: "Pågående",
  completed: "Klar",
  exported: "Exporterad",
};

function formatFiscalYear(start: string, end: string) {
  const s = new Date(start);
  const e = new Date(end);
  if (s.getMonth() === 0 && e.getMonth() === 11) {
    return `${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString("sv-SE")} – ${e.toLocaleDateString("sv-SE")}`;
}

export function AdminCompanyDetailModal({ companyId, onClose, onUpdated }: AdminCompanyDetailModalProps) {
  const [company, setCompany] = useState<AdminCompanyDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Edit form state
  const [name, setName] = useState("");
  const [organizationNumber, setOrganizationNumber] = useState("");
  const [type, setType] = useState("");
  const [seatMunicipality, setSeatMunicipality] = useState("");

  useEffect(() => {
    loadCompany();
  }, [companyId]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const loadCompany = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminApi.getCompany(companyId);
      if (response.success && response.data) {
        const c = response.data;
        setCompany(c);
        setName(c.name);
        setOrganizationNumber(c.organizationNumber);
        setType(c.type);
        setSeatMunicipality(c.seatMunicipality || "");
      } else {
        setError(response.error?.message || "Kunde inte ladda företag");
      }
    } catch {
      setError("Kunde inte ansluta till servern");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await adminApi.updateCompany(companyId, {
        name,
        organizationNumber,
        type,
        seatMunicipality: seatMunicipality || undefined,
      });
      if (response.success) {
        onUpdated();
        toast.success("Företaget har uppdaterats");
        onClose();
      } else {
        setError(response.error?.message || "Kunde inte uppdatera företag");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Är du säker på att du vill ta bort detta företag?")) return;
    setIsSaving(true);
    setError(null);
    try {
      const response = await adminApi.deleteCompany(companyId);
      if (response.success) {
        onUpdated();
        toast.success("Företaget har tagits bort");
        await loadCompany();
      } else {
        toast.error(response.error?.message || "Kunde inte ta bort företag");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await adminApi.restoreCompany(companyId);
      if (response.success) {
        onUpdated();
        toast.success("Företaget har återställts");
        await loadCompany();
      } else {
        toast.error(response.error?.message || "Kunde inte återställa företag");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Företagsdetaljer</h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error && !company ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
            </div>
          ) : company ? (
            <div className="space-y-5">
              {/* Company name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Företagsnamn</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Org number + Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Org.nummer</label>
                  <input
                    type="text"
                    value={organizationNumber}
                    onChange={(e) => setOrganizationNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bolagsform</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Seat municipality */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Säte (kommun)</label>
                <input
                  type="text"
                  value={seatMunicipality}
                  onChange={(e) => setSeatMunicipality(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="T.ex. Stockholm"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <div className="flex items-center gap-3">
                  {company.deletedAt ? (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        Borttaget
                      </span>
                      <button
                        type="button"
                        onClick={handleRestore}
                        disabled={isSaving}
                        className="text-sm text-green-600 hover:text-green-700 font-medium"
                      >
                        Återställ företag
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Aktivt
                      </span>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isSaving}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Ta bort företag
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* ID & Dates */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-xs text-gray-500">
                  <span className="font-medium">ID:</span> {company.id}
                </p>
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Skapad:</span>{" "}
                  {new Date(company.createdAt).toLocaleString("sv-SE")}
                </p>
                {company.deletedAt && (
                  <p className="text-xs text-red-500">
                    <span className="font-medium">Borttagen:</span>{" "}
                    {new Date(company.deletedAt).toLocaleString("sv-SE")}
                  </p>
                )}
              </div>

              {/* Members */}
              {company.members.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <Users size={14} className="text-gray-400" />
                    Medlemmar ({company.members.length})
                  </h3>
                  <div className="space-y-2">
                    {company.members.map((m) => (
                      <div key={m.userId} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <UserCircle size={14} className="text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {m.firstName || m.lastName
                              ? `${m.firstName || ""} ${m.lastName || ""}`.trim()
                              : m.email}
                          </p>
                          <p className="text-xs text-gray-500">{m.email}</p>
                        </div>
                        <span className="text-xs text-gray-400 capitalize">{m.role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Board members */}
              {company.board.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <UserCircle size={14} className="text-gray-400" />
                    Styrelse ({company.board.length})
                  </h3>
                  <div className="space-y-2">
                    {company.board.map((b) => (
                      <div key={b.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{b.name}</p>
                        </div>
                        <span className="text-xs text-gray-400 capitalize">{b.role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reports */}
              {company.reports.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <FileText size={14} className="text-gray-400" />
                    Årsredovisningar ({company.reports.length})
                  </h3>
                  <div className="space-y-2">
                    {company.reports.map((r) => (
                      <div key={r.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {formatFiscalYear(r.fiscalYearStart, r.fiscalYearEnd)}
                          </p>
                          <p className="text-xs text-gray-500">Steg {r.currentStep}/5</p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {statusLabels[r.status] || r.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {company && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
            <Button variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Spinner size="sm" /> : <Save size={16} />}
              Spara
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
